// App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
} from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import { LiveStatusProvider } from "./context/LiveStatusContext";
import { useAuth } from "./hooks/useAuth";
import AdminRoute from "./routes/guards/AdminRoute";
import PublicOnlyRoute from "./routes/guards/PublicRoute";
import ProtectedRoute from "./routes/guards/ProtectedRoute";

import {
  PageTransitionWrapper,
  LoginFacebook,
  Header,
  Footer,
} from "./components";

//import { PrivacyPolicy } from "./pages";

import "./App.css";
import "./index.css";
import "bootstrap/dist/css/bootstrap.min.css";

import AudioLayout from "./pages/audiolectures/AudioLayout";

// Lazy pages
const Home = React.lazy(() => import("./pages/home/Home"));
const Signup = React.lazy(() => import("./pages/signup/Signup"));
const Login = React.lazy(() => import("./pages/login/Login"));
const ForgotPassword = React.lazy(
  () => import("./pages/forgotpassword/ForgotPassword"),
);
const ResetPassword = React.lazy(
  () => import("./pages/resetpassword/ResetPassword"),
);
const LiveStream = React.lazy(() => import("./pages/livestream/LiveStream"));
const AdminApp = React.lazy(() => import("./admin/AdminApp")); // <-- Admin handles its own routes
const PrivacyPolicy = React.lazy(
  () => import("./pages/privacypolicy/PrivacyPolicy"),
);
const TermsOfSerivce = React.lazy(() => import("./pages/terms/TermsOfService"));
const Settings = React.lazy(() => import("./pages/settings/Settings"));
const ProfilePage = React.lazy(() => import("./pages/profilepage/ProfilePage"));
const DataDeletion = React.lazy(
  () => import("./pages/datadeletion/DataDeletion"),
);

const Video = React.lazy(() => import("./pages/video/Video"));

const AudioLectures = React.lazy(
  () => import("./pages/audiolectures/AudioLectures"),
);
const LectureDetail = React.lazy(
  () => import("./pages/audiolectures/LectureDetail"),
);

const Library = React.lazy(() => import("./pages/library/Library"));
const Playlists = React.lazy(() => import("./pages/playlists/Playlists"));
const PlaylistDetail = React.lazy(() => import("./pages/playlists/PlaylistDetail"));

const VideoWatch = React.lazy(() => import("./pages/videowatch/VideoWatch"));
// V2 player (Vidstack) — parallel route while it is being evaluated.
const VideoWatchV2 = React.lazy(() =>
  import("./pages/videowatch/VideoWatchV2")
);

/** Redirect legacy /audio-lectures/:id → /audio/:id (preserves the lecture id) */
function AudioDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/audio/${id}`} replace />;
}

/** Public site layout: always shows Header + Footer */
function SiteLayout() {
  return (
    <div className="site-layout">
      <Header />
      <main className="site-layout__main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function HeaderOnlyLayout() {
  return (
    <>
      <Header />
      <Outlet />
    </>
  );
}

function AppRoutes() {
  const auth = useAuth();
  return (
    <PageTransitionWrapper>
      <LiveStatusProvider
        auth={{ isAuthenticated: auth.isAuthenticated, token: auth.token }}
      >
        <Routes>
          {/* Public app (with Header/Footer via layout) */}
          <Route element={<SiteLayout />}>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/video"
              element={
                <ProtectedRoute>
                  <Video />
                </ProtectedRoute>
              }
            />

            {/* Legacy routes now unified under /video (filter chips) */}
            <Route
              path="/video-lectures"
              element={<Navigate to="/video" replace />}
            />
            <Route
              path="/past-streams"
              element={<Navigate to="/video" replace />}
            />
            <Route
              path="/annual-events"
              element={<Navigate to="/video" replace />}
            />
            <Route
              path="/mehfil-e-milaad"
              element={<Navigate to="/video" replace />}
            />
            <Route
              path="/mehfil-e-samaa"
              element={<Navigate to="/video" replace />}
            />

            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <Library />
                </ProtectedRoute>
              }
            />

            {/* Playlists share AudioLayout so the bottom player keeps playing
                while moving between Audio and Playlists. */}
            <Route element={<AudioLayout />}>
              <Route path="/audio" element={<AudioLectures />} />
              <Route path="/audio/:id" element={<LectureDetail />} />
              <Route
                path="/playlists"
                element={
                  <ProtectedRoute>
                    <Playlists />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/playlists/:id"
                element={
                  <ProtectedRoute>
                    <PlaylistDetail />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Legacy audio routes → /audio */}
            <Route
              path="/audio-lectures"
              element={<Navigate to="/audio" replace />}
            />
            <Route
              path="/audio-lectures/:id"
              element={<AudioDetailRedirect />}
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/signup"
              element={
                <PublicOnlyRoute>
                  <Signup />
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicOnlyRoute>
                  <Login />
                </PublicOnlyRoute>
              }
            />

            <Route
              path="/reset-password"
              element={
                <ProtectedRoute>
                  <ResetPassword />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route element={<HeaderOnlyLayout />}>
            <Route
              path="/live"
              element={
                <ProtectedRoute>
                  <LiveStream />
                </ProtectedRoute>
              }
            />

            <Route
              path="/video-lectures/watch/:livestreamId"
              element={
                <ProtectedRoute>
                  <VideoWatchV2 />
                </ProtectedRoute>
              }
            />

            {/* Previous player, kept reachable for comparison / rollback.
                Delete this route and pages/videowatch/VideoWatch.jsx once V2
                is settled. */}
            <Route
              path="/video-lectures/watch-legacy/:livestreamId"
              element={
                <ProtectedRoute>
                  <VideoWatch />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/terms" element={<TermsOfSerivce />} />
          {/* Admin app: render AdminApp directly so it controls its own nested routes */}
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminApp />
              </AdminRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </LiveStatusProvider>
    </PageTransitionWrapper>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <React.Suspense fallback={<div>Loading...</div>}>
          <AppRoutes />
        </React.Suspense>
      </AuthProvider>
    </Router>
  );
}
